// @ts-strict-ignore
import Cookie from '@carvana/stc-cookie';
import { getUserState } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';

import AnalyticsApi from '../api/AnalyticsApi';

type EventSourceContext = {
  eventSource?: string;
  eventAction?: string;
  eventSourceName?: string;
  eventSourceId?: string;
  eventTime: Date;
  eventName: string;
};

type CustomAttributes = {
  [key: string]: unknown;
};

export type ClickStreamEvent = {
  visitorContext: {
    browserCookieId: string;
    userId: string;
    userAgent: string;
    clientIp: string;
  };
  browserContext: BrowserData;
  eventSourceContext: EventSourceContext;
  customAttributes: CustomAttributes;
};

export type CarouselEventData = {
  userInteracted: boolean;
  reachedEnd: boolean;
  totalSlides: number;
  visibleSlides: number;
};

type BrowserData = {
  referrerUrl: string;
  url: string;
  browserName: string;
  osName: string;
  deviceCategory: string | null;
  deviceName: string | null;
  language: string;
  screenResolution: string;
  browserResolution: string;
};

enum EventActions {
  Changed = 'changed',
  Clicked = 'clicked',
  Visited = 'page_visited',
  Scrolled = 'scrolled',
  Bucketed = 'bucketed',
  Errored = 'errored',
  Recovered = 'recovered'
}

enum EventSources {
  Redux = 'system',
  UserInteraction = 'user_interaction'
}

enum EventNames {
  Changed = 'Changed',
  Clicked = 'Clicked',
  Visited = 'Visited',
  Scrolled = 'Scrolled',
  Bucketed = 'Bucketed'
}

const getOsName = () => {
  const appVersion = window?.navigator?.appVersion || '';

  if (appVersion.indexOf('Win') !== -1) {
    return 'Windows';
  }
  if (appVersion.indexOf('iPhone OS') !== -1) {
    return 'iPhone OS';
  }
  if (appVersion.indexOf('Mac') !== -1) {
    return 'MacOS';
  }
  if (appVersion.indexOf('X11') !== -1) {
    return 'UNIX';
  }
  if (appVersion.indexOf('Linux') !== -1) {
    return 'Linux';
  }
  return `${window?.navigator?.platform || 'Unknown OS'}`;
};

const getBrowserName = () => {
  const useragent = window.navigator.userAgent;
  let matchedName =
    useragent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(matchedName[1])) {
    const ieRevision = /\brv[ :]+(\d+)/g.exec(useragent) || [];
    return `IE ${ieRevision[1] || ''}`;
  }
  if (matchedName[1] === 'Chrome') {
    const operaRevision = useragent.match(/\bOPR\/(\d+)/);
    if (operaRevision !== null) {
      return `Opera ${operaRevision[1]}`;
    }
    const edgeRevision = useragent.match(/\bEdg\/(\d+)/);
    if (edgeRevision !== null) {
      return `Edge ${edgeRevision[1]}`;
    }
  }
  matchedName = matchedName[2]
    ? [matchedName[1], matchedName[2]]
    : [window.navigator.appName, window.navigator.appVersion, '-?'];
  const revision = useragent.match(/version\/(\d+)/i);
  if (revision !== null) {
    matchedName.splice(1, 1, revision[1]);
  }

  return matchedName.join(' ');
};

const getBrowserData = (): BrowserData => ({
  browserName: getBrowserName(),
  osName: getOsName(),
  browserResolution: `${window.innerWidth}x${window.innerHeight}`,
  screenResolution: `${window.screen.width}x${window.screen.height}`,
  /* eslint-disable camelcase */
  deviceCategory: window.WURFL?.complete_device_name,
  deviceName: window.WURFL?.form_factor,
  /* eslint-enable */
  language: window.navigator.language,
  referrerUrl: window.document.referrer,
  url: window.location.href
});

const makeEventName = (eventName: string) => `SellToCarvana - ${eventName}`;

const getIpAddress = () => Cookie.getCookie('CVCurrentIpAddress') || '';

const makeEvent = (
  eventSourceContext: EventSourceContext,
  customAttributes: CustomAttributes = {}
): ClickStreamEvent => ({
  visitorContext: {
    browserCookieId: Cookie.getCookie('BrowserCookieId'),
    userId: getUserState().user.user_id || null,
    userAgent: window.navigator.userAgent,
    clientIp: getIpAddress()
  },
  browserContext: getBrowserData(),
  eventSourceContext,
  customAttributes: {
    ...customAttributes
  }
});

const recordMoMetricsEvent = (
  eventSourceContext: EventSourceContext,
  customAttributes: CustomAttributes = {},
  sendImmediately = false
): ClickStreamEvent | Promise<unknown> | void => {
  const event = makeEvent(eventSourceContext, customAttributes);
  if (sendImmediately) {
    return AnalyticsApi.recordNow(event);
  }

  datadogRum?.addAction(eventSourceContext.eventName);
  AnalyticsApi.queue(event);
  return event;
};

const makeEventNameAndTime = (
  eventName: string,
  details: Partial<EventSourceContext>
): EventSourceContext => ({
  eventName: makeEventName(eventName),
  eventTime: new Date(),
  ...details
});

const recordActionClicked = (
  pageName: string,
  component: string,
  eventClickedData = {},
  sendImmediately?: boolean
) => {
  const eventName = component ? `${pageName}:${component}` : pageName;
  return recordMoMetricsEvent(
    makeEventNameAndTime(`${eventName} - ${EventNames.Clicked}`, {
      eventAction: EventActions.Clicked,
      eventSourceName: component ? `Component:${component}` : `Page:${pageName}`,
      eventSource: EventSources.UserInteraction
    }),
    eventClickedData,
    sendImmediately
  );
};

const recordGenericEvent = (
  pageName: string,
  component: string,
  eventAction: EventActions,
  eventClickedData = {},
  sendImmediately?: boolean
) => {
  const eventName = component ? `${pageName}:${component}` : pageName;
  return recordMoMetricsEvent(
    makeEventNameAndTime(`${eventName} - ${eventAction}`, {
      eventAction,
      eventSourceName: component ? `Component:${component}` : `Page:${pageName}`,
      eventSource: EventSources.UserInteraction
    }),
    eventClickedData,
    sendImmediately
  );
};

const recordEventAndNavigate = async (
  event,
  pageName: string,
  component: string,
  eventClickedData = {}
) => {
  const { href } = event.currentTarget;
  event.preventDefault();
  await recordActionClicked(pageName, component, eventClickedData, true);
  window.location.href = href;
};

export {
  recordActionClicked,
  recordGenericEvent,
  makeEvent,
  recordEventAndNavigate,
  recordMoMetricsEvent,
  makeEventNameAndTime,
  EventSources,
  EventActions
};
